import { GitBranch, Github, Gitlab } from 'lucide-react';
import { GIT_PROVIDER_LABELS, type GitProvider } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { normalizeRepoSlug } from '../../lib/repo-slug';

export interface RepoLabelProps {
  /** Repo reference: a bare `owner/repo` slug or a full GitHub URL. */
  repo: string;
  /** Optional branch, shown as `@branch` after the slug. */
  branch?: string | null;
  /**
   * When set, the label renders as a link to this URL (new tab). Pass
   * `repoUrl(repo)` to link straight to the repository. Omit for a plain,
   * non-navigating label (e.g. inside an already-clickable row).
   */
  href?: string | null;
  /** Git host whose mark and accessible label should be rendered. */
  provider?: GitProvider;
  /** Sizing/color overrides for the provider mark. Defaults to `icon-xs`. */
  iconClassName?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Shared inline "git repository" reference: the GitHub mark (the same
 * `lucide-react` octocat the agent rows use) followed by the normalized
 * `owner/repo` slug, with an optional `@branch`. Every surface that shows a
 * repo (agent meta line, PR hover card, filters, dashboard) should render
 * through this so the icon, slug normalization, and link behavior stay
 * identical instead of drifting across hand-rolled copies.
 */
export function RepoLabel({
  repo,
  branch,
  href,
  provider = 'github',
  iconClassName,
  className,
  'data-testid': dataTestId,
}: RepoLabelProps) {
  const slug = normalizeRepoSlug(repo);
  const ProviderIcon = provider === 'gitlab' ? Gitlab : Github;
  const providerLabel = GIT_PROVIDER_LABELS[provider];
  const body = (
    <>
      <ProviderIcon className={cn('icon-xs shrink-0', iconClassName)} aria-hidden="true" />
      <span className="truncate">{slug}</span>
      {branch ? (
        <span className="flex min-w-0 items-center gap-0.5 text-muted-foreground">
          <GitBranch className="icon-xs shrink-0" aria-hidden="true" />
          <span className="truncate">{branch}</span>
        </span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${slug} on ${providerLabel}`}
        data-testid={dataTestId}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex min-w-0 items-center gap-1 rounded-sm transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        {body}
      </a>
    );
  }

  return (
    <span
      data-testid={dataTestId}
      className={cn('inline-flex min-w-0 items-center gap-1', className)}
    >
      {body}
    </span>
  );
}
